import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
    Grid,
    Typography,
    Paper,
    Avatar,
    InputAdornment,
    Fade
} from '@mui/material';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EuroIcon from '@mui/icons-material/Euro';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import { Enrollment } from '../../types/enrollment';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { addDays, addMonths, format } from 'date-fns';
import { fr } from 'date-fns/locale';

interface EnrollmentEditModalProps {
    open: boolean;
    onClose: () => void;
    enrollment: Enrollment | null;
    onUpdated: () => void;
}

const EnrollmentEditModal: React.FC<EnrollmentEditModalProps> = ({ open, onClose, enrollment, onUpdated }) => {
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState({
        startDate: '',
        endDate: '',
        depositPaid: 0,
        totalPaid: 0,
        expectedDepositAmount: 0,
        expectedTotalAmount: 0,
        depositExpiryDate: '',
        totalExpiryDate: '',
        status: '',
        minHours: 0
    });

    const { showToast } = useToast();
    const { user: currentUser } = useAuth();
    const isOfAdmin = currentUser?.role === 'of-admin';

    const offerPrice = enrollment?.offer?.price || 0;
    const expectedDeposit = Math.round(offerPrice * 0.3);
    const expectedTotal = Math.round(offerPrice * 0.7);

    const enrollmentDate = enrollment?.createdAt ? new Date(enrollment.createdAt) : new Date();
    const depositExpiry = addDays(enrollmentDate, 31);
    const totalExpiry = addMonths(enrollmentDate, 9);

    useEffect(() => {
        if (enrollment) {
            setForm({
                startDate: enrollment.startDate ? new Date(enrollment.startDate).toISOString().split('T')[0] : '',
                endDate: enrollment.endDate ? new Date(enrollment.endDate).toISOString().split('T')[0] : '',
                depositPaid: enrollment.depositPaid || 0,
                totalPaid: enrollment.totalPaid || 0,
                expectedDepositAmount: enrollment.expectedDepositAmount !== undefined ? enrollment.expectedDepositAmount : expectedDeposit,
                expectedTotalAmount: enrollment.expectedTotalAmount !== undefined ? enrollment.expectedTotalAmount : expectedTotal,
                depositExpiryDate: enrollment.depositExpiryDate ? new Date(enrollment.depositExpiryDate).toISOString().split('T')[0] : format(depositExpiry, 'yyyy-MM-dd'),
                totalExpiryDate: enrollment.totalExpiryDate ? new Date(enrollment.totalExpiryDate).toISOString().split('T')[0] : format(totalExpiry, 'yyyy-MM-dd'),
                status: enrollment.status,
                minHours: enrollment.minHours || 0
            });
        }
    }, [enrollment]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectChange = (e: any) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!enrollment) return;
        setLoading(true);
        try {
            await api.put(`/api/enrollment/${enrollment._id}`, {
                startDate: form.startDate,
                endDate: form.endDate,
                depositPaid: Number(form.depositPaid),
                totalPaid: Number(form.totalPaid),
                expectedDepositAmount: Number(form.expectedDepositAmount),
                expectedTotalAmount: Number(form.expectedTotalAmount),
                depositExpiryDate: form.depositExpiryDate,
                totalExpiryDate: form.totalExpiryDate,
                status: form.status,
                minHours: Number(form.minHours)
            });
            showToast('Inscription mise à jour avec succès', 'success');
            onUpdated();
            onClose();
        } catch (err) {
            console.error('Error updating enrollment', err);
            showToast('Erreur lors de la mise à jour', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!enrollment) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
        >
            <Grid container sx={{ minHeight: '500px' }}>
                {/* Left Column - Form */}
                <Grid size={{ md: 7 }} sx={{ p: 4, bgcolor: '#ffffff' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand', color: '#1a202c', mb: 1 }}>
                            Modifier l'inscription
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#718096', fontFamily: 'Quicksand' }}>
                            Mettez à jour les dates, les paiements ou le statut.
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Dates */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                Planification
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <TextField
                                        label="Début"
                                        type="date"
                                        name="startDate"
                                        value={form.startDate}
                                        onChange={handleChange}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <TextField
                                        label="Fin"
                                        type="date"
                                        name="endDate"
                                        value={form.endDate}
                                        onChange={handleChange}
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{ sx: { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Financials */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                Paiements
                            </Typography>
                            <Grid container spacing={3}>
                                <Grid size={{ xs: 6 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label="Acompte Payé"
                                            type="number"
                                            name="depositPaid"
                                            value={form.depositPaid}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                            disabled={isOfAdmin}
                                            InputProps={{
                                                sx: { borderRadius: 2 },
                                                startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment>
                                            }}
                                        />
                                        {currentUser?.role === 'super-admin' ? (
                                            <>
                                                <TextField
                                                    label="Acompte Attendu"
                                                    type="number"
                                                    name="expectedDepositAmount"
                                                    value={form.expectedDepositAmount}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{
                                                        sx: { borderRadius: 2 },
                                                        startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment>
                                                    }}
                                                />
                                                <TextField
                                                    label="Échéance Acompte"
                                                    type="date"
                                                    name="depositExpiryDate"
                                                    value={form.depositExpiryDate}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ sx: { borderRadius: 2 } }}
                                                />
                                            </>
                                        ) : (
                                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" display="block" color="primary.main" fontWeight={600}>
                                                    Attendu : {form.expectedDepositAmount}€
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Échéance : {form.depositExpiryDate ? format(new Date(form.depositExpiryDate), 'dd/MM/yyyy') : ''}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 6 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label="Total Payé"
                                            type="number"
                                            name="totalPaid"
                                            value={form.totalPaid}
                                            onChange={handleChange}
                                            fullWidth
                                            size="small"
                                            disabled={isOfAdmin}
                                            InputProps={{
                                                sx: { borderRadius: 2 },
                                                startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment>
                                            }}
                                        />
                                        {currentUser?.role === 'super-admin' ? (
                                            <>
                                                <TextField
                                                    label="Total Attendu"
                                                    type="number"
                                                    name="expectedTotalAmount"
                                                    value={form.expectedTotalAmount}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputProps={{
                                                        sx: { borderRadius: 2 },
                                                        startAdornment: <InputAdornment position="start"><EuroIcon fontSize="small" /></InputAdornment>
                                                    }}
                                                />
                                                <TextField
                                                    label="Échéance Totale"
                                                    type="date"
                                                    name="totalExpiryDate"
                                                    value={form.totalExpiryDate}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{ shrink: true }}
                                                    InputProps={{ sx: { borderRadius: 2 } }}
                                                />
                                            </>
                                        ) : (
                                            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" display="block" color="primary.main" fontWeight={600}>
                                                    Attendu : {form.expectedTotalAmount}€
                                                </Typography>
                                                <Typography variant="caption" display="block" color="text.secondary">
                                                    Échéance : {form.totalExpiryDate ? format(new Date(form.totalExpiryDate), 'dd/MM/yyyy') : ''}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                            </Grid>
                        </Box>

                        {/* Status */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                État
                            </Typography>
                            <FormControl fullWidth size="small">
                                <InputLabel>Statut</InputLabel>
                                <Select
                                    name="status"
                                    value={form.status}
                                    label="Statut"
                                    onChange={handleSelectChange}
                                    startAdornment={<InputAdornment position="start"><ToggleOnIcon fontSize="small" /></InputAdornment>}
                                    sx={{ borderRadius: 2 }}
                                >
                                    <MenuItem value="pending">En attente</MenuItem>
                                    <MenuItem value="active">Actif</MenuItem>
                                    <MenuItem value="completed">Terminé</MenuItem>
                                    <MenuItem value="expired">Expiré</MenuItem>
                                    <MenuItem value="cancelled">Annulé</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                Paramètres Spécifiques
                            </Typography>
                            <TextField
                                label="Heures minimales (financeur)"
                                type="number"
                                name="minHours"
                                value={form.minHours}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                InputProps={{ sx: { borderRadius: 2 } }}
                            />
                        </Box>

                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={onClose} sx={{ color: '#718096', fontWeight: 600 }}>Annuler</Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={loading}
                                sx={{
                                    bgcolor: '#667eea',
                                    fontWeight: 700,
                                    borderRadius: 2,
                                    px: 4,
                                    boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.5)',
                                    '&:hover': { bgcolor: '#5a67d8' }
                                }}
                            >
                                {loading ? <CircularProgress size={24} color="inherit" /> : 'Enregistrer'}
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column - Info Summary */}
                <Grid size={{ md: 5 }} sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        bottom: -30,
                        right: -30,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                    }} />

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon /> Contexte
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Apprenant</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                <Avatar sx={{ bgcolor: 'white', color: '#667eea' }}>
                                    {enrollment.learner?.firstName?.[0] || 'U'}
                                </Avatar>
                                <Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {enrollment.learner ? `${enrollment.learner.firstName} ${enrollment.learner.lastName}` : 'Utilisateur supprimé'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                        {enrollment.learner?.email}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Parcours</Typography>
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {enrollment.parcours?.title || 'Titre indisponible'}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {enrollment.parcours?.description ? `${enrollment.parcours.description.substring(0, 80)}...` : 'Pas de description'}
                                </Typography>
                            </Box>
                        </Paper>

                        <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                            <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Dates Actuelles</Typography>
                            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarTodayIcon fontSize="small" sx={{ opacity: 0.8 }} />
                                <Box>
                                    <Typography variant="body2">
                                        Du {new Date(enrollment.startDate).toLocaleDateString('fr-FR')} au {new Date(enrollment.endDate).toLocaleDateString('fr-FR')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                </Grid>
            </Grid>
        </Dialog>
    );
};

export default EnrollmentEditModal;
